import { useCallback, useEffect, useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Body1,
  Caption1,
  Button,
  Card,
  Combobox,
  Dropdown,
  Option,
  Textarea,
  Input,
  Label,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  ProgressBar,
  Badge,
} from '@fluentui/react-components';
import { transformrBrand } from '@/theme/transformrTheme';
import { PageBanner } from '@/components/common/PageBanner';
import {
  ArrowRightRegular,
  ArrowDownloadRegular,
  ArrowResetRegular,
  ShieldLockRegular,
  CheckmarkCircleRegular,
  InfoRegular,
  PersonKeyRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/hooks/useAuth';
import { useGraphConsent } from '@/features/discovery/hooks/useGraphConsent';
import { getProfile } from '@/features/discovery/services/discoveryApi';
import { useDeepAnalysis } from './hooks/useDeepAnalysis';
import type { Industry, UserContext, AnalysisStep } from '@/features/discovery/types/discovery.types';
import { COMPANY_SIZE_OPTIONS, INDUSTRY_OPTIONS, USER_ROLE_OPTIONS } from '@/features/discovery/types/discovery.types';

const COUNTRY_OPTIONS = [
  'Australia',
  'New Zealand',
  'United Kingdom',
  'United States',
  'Canada',
  'Singapore',
  'Other',
] as const;

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  heroBanner: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
    padding: '28px 32px',
    borderRadius: tokens.borderRadiusXLarge,
    background: `linear-gradient(135deg, ${transformrBrand.blueDeep} 0%, ${tokens.colorBrandBackground} 50%, ${transformrBrand.greenLight} 100%)`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  heroTitle: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
    margin: 0,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  heroSubtitle: {
    color: tokens.colorNeutralForegroundOnBrand,
    opacity: 0.9,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: tokens.spacingHorizontalXXL,
    alignItems: 'start',
    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },
  formCard: {
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
    borderTop: `3px solid ${tokens.colorBrandBackground}`,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  sidebarCard: {
    padding: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalSNudge,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
    fontSize: tokens.fontSizeBase200,
  },
  submitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalXS,
  },
  customIndustryField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  // Progress & results
  progressCard: {
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  stepComplete: {
    color: tokens.colorBrandForeground1,
  },
  resultCard: {
    padding: tokens.spacingHorizontalXXL,
    textAlign: 'center',
  },
  scoreDisplay: {
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
    lineHeight: tokens.lineHeightHero1000,
  },
  tierBadge: {
    marginTop: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalL,
  },
});

const DEEP_ANALYSIS_FEATURES = [
  'Licence utilisation & cost analysis',
  'Microsoft Secure Score with categories',
  'Conditional Access policy review',
  'MFA & passwordless adoption metrics',
  'Copilot readiness assessment',
  'M365 app update channel compliance',
  'Sensitivity label & DLP review',
  'External sharing & guest user audit',
  'Directory audit activity sampling',
  'Workload adoption metrics',
  'SharePoint content structure',
  'AI readiness scoring (5-pillar)',
];

const STEP_LABELS: Record<string, string> = {
  authenticate: 'Authenticating',
  scan_sites: 'Scanning admin data (licences, security, identity)',
  scan_files: 'Scanning SharePoint content',
  scan_content_types: 'Analysing content structure',
  calculate_score: 'Calculating AI readiness score',
  generate_ai_content: 'Generating AI-powered insights',
  generate_report: 'Building your report',
  upload_report: 'Storing report',
};

export function DeepAnalysisPage() {
  const styles = useStyles();
  const { user } = useAuth();
  const { acquireApiToken } = useGraphConsent();
  const deep = useDeepAnalysis();

  // Form state (same fields as HomePage)
  const [industry, setIndustry] = useState<Industry | ''>('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [userRole, setUserRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [country, setCountry] = useState('Australia');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [aiGoals, setAiGoals] = useState('');

  // Profile loading
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Load saved profile on mount
  useEffect(() => {
    if (profileLoaded || !user) return;
    let cancelled = false;

    async function loadProfile() {
      setIsLoadingProfile(true);
      try {
        const apiToken = await acquireApiToken();
        if (!apiToken || cancelled) return;
        const profileResult = await getProfile(apiToken);
        if (cancelled) return;
        if (profileResult?.profile) {
          const p = profileResult.profile;
          if (p.industry) setIndustry(p.industry as Industry);
          if (p.custom_industry) setCustomIndustry(p.custom_industry);
          if (p.company_name) setCompanyName(p.company_name);
          if (p.company_size) setCompanySize(p.company_size);
          if (p.user_role) {
            const knownRole = USER_ROLE_OPTIONS.find((o) => o.value === p.user_role);
            if (knownRole) {
              setUserRole(p.user_role);
            } else {
              setUserRole('other');
              setCustomRole(p.user_role);
            }
          }
          if (p.country) setCountry(p.country);
          if (p.website_url) setWebsiteUrl(p.website_url);
          if (p.ai_goals) setAiGoals(p.ai_goals);
        }
      } catch {
        // Profile loading is best-effort
      } finally {
        if (!cancelled) {
          setIsLoadingProfile(false);
          setProfileLoaded(true);
        }
      }
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [user, profileLoaded, acquireApiToken]);

  const isValid = industry !== '' && companyName.trim() !== '' && companySize !== ''
    && userRole !== '' && (userRole !== 'other' || customRole.trim() !== '');

  const handleSubmit = useCallback(() => {
    if (!isValid || !industry) return;
    const userContext: UserContext = {
      user_role: userRole === 'other' ? customRole.trim() : userRole,
      company_size: companySize,
      ai_goals: aiGoals,
      company_name: companyName.trim(),
      country,
      ...(websiteUrl.trim() ? { website_url: websiteUrl.trim() } : {}),
      ...(industry === 'other' && customIndustry.trim() ? { custom_industry: customIndustry.trim() } : {}),
    };
    deep.startAnalysis(industry, userContext);
  }, [industry, customIndustry, userRole, customRole, companySize, aiGoals, companyName, country, websiteUrl, isValid, deep]);

  // Loading state
  if (deep.isCheckingEligibility) {
    return (
      <div className={styles.page}>
        <Spinner label="Checking eligibility..." />
      </div>
    );
  }

  // Not eligible
  if (deep.isEligible === false) {
    return (
      <div className={styles.page}>
        <PageBanner
          kicker="AI Readiness · Deep Scan"
          title="Deep Analysis"
          subtitle="Premium M365 assessment for Transformr customers."
        />
        <Card className={styles.formCard}>
          <Text size={400} weight="semibold">
            Deep Analysis is not enabled for your organisation
          </Text>
          <Body1 style={{ color: tokens.colorNeutralForeground2 }}>
            Deep Analysis provides admin-level insights into your Microsoft 365 environment
            including licensing, security posture, and governance assessment.
            Contact your Transformr representative or email{' '}
            <a href="mailto:hello@transformr.com.au" style={{ color: tokens.colorBrandForeground1 }}>
              hello@transformr.com.au
            </a>{' '}
            to enable it.
          </Body1>
        </Card>
      </div>
    );
  }

  // Analysis in progress
  if (deep.analysisId && deep.status && deep.status !== 'completed' && deep.status !== 'failed') {
    return (
      <div className={styles.page}>
        <PageBanner
          kicker="AI Readiness · Deep Scan"
          title="Deep Analysis"
          subtitle="Analysing your environment…"
        />

        <Card className={styles.progressCard}>
          <ProgressBar value={deep.progressPct / 100} thickness="large" color="brand" />
          <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
            {deep.message || STEP_LABELS[deep.currentStep || ''] || 'Processing...'}
          </Text>
          <div className={styles.stepList}>
            {Object.entries(STEP_LABELS).map(([key, label]) => {
              const isComplete = deep.stepsCompleted.includes(key as AnalysisStep);
              const isCurrent = deep.currentStep === key;
              return (
                <div key={key} className={`${styles.step} ${isComplete ? styles.stepComplete : ''}`}>
                  {isComplete ? <CheckmarkCircleRegular /> : isCurrent ? <Spinner size="tiny" /> : <span style={{ width: '16px' }} />}
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  }

  // Analysis failed
  if (deep.status === 'failed') {
    return (
      <div className={styles.page}>
        <PageBanner kicker="AI Readiness · Deep Scan" title="Deep Analysis" />
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Analysis Failed</MessageBarTitle>
            {deep.error || 'An unexpected error occurred. This may be because admin consent has not been granted.'}
          </MessageBarBody>
        </MessageBar>
        <div className={styles.actions}>
          <Button appearance="secondary" icon={<PersonKeyRegular />} onClick={deep.triggerAdminConsent}>
            Grant Admin Consent
          </Button>
          <Button appearance="primary" icon={<ArrowResetRegular />} onClick={deep.reset}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Analysis complete — show results
  if (deep.reportReady && deep.analysisId) {
    return (
      <div className={styles.page}>
        <PageBanner kicker="AI Readiness · Deep Scan" title="Deep Analysis Complete" />

        <Card className={styles.resultCard}>
          {deep.score !== null && (
            <>
              <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>AI Readiness Score</Text>
              <div className={styles.scoreDisplay}>{deep.score}</div>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>out of 100</Text>
              {deep.tier && (
                <div className={styles.tierBadge}>
                  <Badge
                    appearance="filled"
                    color={deep.tier === 'Advanced' ? 'success' : deep.tier === 'Ready' ? 'brand' : deep.tier === 'Developing' ? 'warning' : 'informative'}
                    size="large"
                  >
                    {deep.tier}
                  </Badge>
                </div>
              )}
            </>
          )}
        </Card>
        <div className={styles.actions}>
          <Button appearance="primary" size="large" icon={<ArrowDownloadRegular />} onClick={deep.downloadReport}>
            Download Report
          </Button>
          <Button appearance="secondary" icon={<ArrowResetRegular />} onClick={deep.reset}>
            Run Again
          </Button>
        </div>
      </div>
    );
  }

  // Default: show form (matching HomePage layout)
  return (
    <div className={styles.page}>
      <PageBanner
        kicker="AI Readiness · Deep Scan"
        title={user?.displayName ? `Welcome, ${user.displayName.split(' ')[0]}` : 'Deep Analysis'}
        subtitle="Comprehensive admin-level assessment of your Microsoft 365 environment covering licensing, security, governance, collaboration, and AI readiness."
      />


      <div className={styles.layout}>
        {/* Left column — form */}
        <Card className={styles.formCard}>
          <Text size={400} weight="semibold">
            Tell us about your organisation
          </Text>

          {isLoadingProfile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              <Caption1>Loading your saved details...</Caption1>
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <Label required htmlFor="da-industry">Industry</Label>
              <Combobox
                id="da-industry"
                placeholder="Search or select your industry"
                value={industry ? INDUSTRY_OPTIONS.find((o) => o.value === industry)?.label ?? '' : ''}
                selectedOptions={industry ? [industry] : []}
                onOptionSelect={(_, data) => {
                  setIndustry(data.optionValue as Industry);
                  if (data.optionValue !== 'other') setCustomIndustry('');
                }}
                freeform={false}
              >
                {INDUSTRY_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    <div>
                      <Text size={300} weight="semibold">{opt.label}</Text>
                      <br />
                      <Caption1>{opt.description}</Caption1>
                    </div>
                  </Option>
                ))}
              </Combobox>
              {industry === 'other' && (
                <div className={styles.customIndustryField}>
                  <Input
                    placeholder="Describe your industry (optional)"
                    value={customIndustry}
                    onChange={(_, data) => setCustomIndustry(data.value.slice(0, 32))}
                    maxLength={32}
                  />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <Label required htmlFor="da-companyName">Company Name</Label>
              <Input
                id="da-companyName"
                placeholder="e.g. Acme Construction Pty Ltd"
                value={companyName}
                onChange={(_, data) => setCompanyName(data.value.slice(0, 200))}
                maxLength={200}
              />
            </div>

            <div className={styles.field}>
              <Label required htmlFor="da-companySize">Number of Employees</Label>
              <Dropdown
                id="da-companySize"
                placeholder="Select company size"
                value={companySize ? COMPANY_SIZE_OPTIONS.find((o) => o.value === companySize)?.label ?? '' : ''}
                selectedOptions={companySize ? [companySize] : []}
                onOptionSelect={(_, data) => setCompanySize(data.optionValue as string)}
              >
                {COMPANY_SIZE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <div className={styles.field}>
              <Label required htmlFor="da-userRole">Your Role</Label>
              <Dropdown
                id="da-userRole"
                placeholder="Select your role"
                value={userRole ? USER_ROLE_OPTIONS.find((o) => o.value === userRole)?.label ?? '' : ''}
                selectedOptions={userRole ? [userRole] : []}
                onOptionSelect={(_, data) => {
                  setUserRole(data.optionValue as string);
                  if (data.optionValue !== 'other') setCustomRole('');
                }}
              >
                {USER_ROLE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value} text={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
              {userRole === 'other' && (
                <div className={styles.customIndustryField}>
                  <Input
                    placeholder="Describe your role"
                    value={customRole}
                    onChange={(_, data) => setCustomRole(data.value.slice(0, 200))}
                    maxLength={200}
                    required
                  />
                </div>
              )}
            </div>

            <div className={styles.field}>
              <Label htmlFor="da-country">Country</Label>
              <Dropdown
                id="da-country"
                placeholder="Select country"
                value={country}
                selectedOptions={[country]}
                onOptionSelect={(_, data) => setCountry(data.optionValue as string)}
              >
                {COUNTRY_OPTIONS.map((c) => (
                  <Option key={c} value={c} text={c}>{c}</Option>
                ))}
              </Dropdown>
            </div>

            <div className={styles.field}>
              <Label htmlFor="da-websiteUrl">Company Website</Label>
              <Input
                id="da-websiteUrl"
                placeholder="e.g. https://www.example.com.au"
                value={websiteUrl}
                onChange={(_, data) => setWebsiteUrl(data.value.slice(0, 200))}
                maxLength={200}
                type="url"
              />
            </div>

            <div className={mergeClasses(styles.field, styles.fullWidth)}>
              <Label htmlFor="da-aiGoals">How are you thinking about AI?</Label>
              <Textarea
                id="da-aiGoals"
                placeholder="e.g. How do I ensure AI agents only access data they should? Can AI be trusted with sensitive financial or HR information?"
                value={aiGoals}
                onChange={(_, data) => setAiGoals(data.value.slice(0, 4000))}
                resize="vertical"
                rows={6}
                maxLength={4000}
              />
            </div>
          </div>

          {deep.error && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Error</MessageBarTitle>
                {deep.error}
              </MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.submitRow}>
            <Button
              appearance="primary"
              size="large"
              icon={<ArrowRightRegular />}
              iconPosition="after"
              onClick={handleSubmit}
              disabled={!isValid || deep.isAcquiring}
            >
              {deep.isAcquiring ? 'Preparing...' : 'Generate Deep Analysis'}
            </Button>
            <Button
              appearance="subtle"
              icon={<PersonKeyRegular />}
              onClick={deep.triggerAdminConsent}
            >
              Grant Admin Consent
            </Button>
            {!isValid && (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Fill in the required fields to continue
              </Caption1>
            )}
          </div>
        </Card>

        {/* Right column — guidance */}
        <div className={styles.sidebar}>
          <Card className={styles.sidebarCard}>
            <div className={styles.sidebarHeader}>
              <ShieldLockRegular fontSize={20} color={tokens.colorBrandForeground1} />
              <Text weight="semibold" size={300}>What Deep Analysis Covers</Text>
            </div>
            <ul className={styles.featureList}>
              {DEEP_ANALYSIS_FEATURES.map((feature) => (
                <li key={feature} className={styles.featureItem}>
                  <CheckmarkCircleRegular style={{ color: tokens.colorBrandForeground1, flexShrink: 0 }} />
                  <Caption1>{feature}</Caption1>
                </li>
              ))}
            </ul>
          </Card>

          <Card className={styles.sidebarCard}>
            <div className={styles.sidebarHeader}>
              <InfoRegular fontSize={20} color={tokens.colorBrandForeground1} />
              <Text weight="semibold" size={300}>Admin Consent</Text>
            </div>
            <Caption1>
              Deep Analysis requires your tenant administrator to grant
              application-level permissions. If this is your first time,
              click "Grant Admin Consent" before generating the report.
            </Caption1>
            <Caption1>
              This is a one-time step per tenant — once granted, anyone
              in your organisation can run Deep Analysis.
            </Caption1>
          </Card>
        </div>
      </div>
    </div>
  );
}
